import React, { useState, useEffect, useRef } from 'react';
import { VscSettings } from 'react-icons/vsc';
import { MdPrint } from 'react-icons/md';
import { BsFilePdf } from 'react-icons/bs';
import { buildTablePdf } from '../../utils/pdfUtils';
import { getPrinters, submitPrintJob } from '../../api/printJobs';
import './PrintButton.css';

function PrintButton({ title, columns, rows, storageKey, onOpenTab, chartPdf, infoLines }) {
  const defaultVisible = () =>
    columns.reduce((acc, c) => ({ ...acc, [c.key]: c.defaultOn !== false }), {});

  const [visible, setVisible] = useState(() => {
    try {
      const saved = localStorage.getItem(`print_cols_${storageKey}`);
      if (saved) return { ...defaultVisible(), ...JSON.parse(saved) };
    } catch {}
    return defaultVisible();
  });

  const [size, setSize] = useState(
    () => localStorage.getItem(`print_size_${storageKey}`) || 'a4'
  );
  const [duplex, setDuplex] = useState(
    () => localStorage.getItem(`print_duplex_${storageKey}`) === '1'
  );
  const [colsOpen,  setColsOpen]  = useState(false);
  const [menuOpen,  setMenuOpen]  = useState(false);
  const [printers,  setPrinters]  = useState(null);
  const [sending,   setSending]   = useState(false);
  const ref = useRef(null);

  const changeSize = (s) => {
    setSize(s);
    localStorage.setItem(`print_size_${storageKey}`, s);
  };

  const changeDuplex = (d) => {
    setDuplex(d);
    localStorage.setItem(`print_duplex_${storageKey}`, d ? '1' : '0');
  };

  // Close both dropdowns on outside click
  useEffect(() => {
    if (!colsOpen && !menuOpen) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setColsOpen(false);
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [colsOpen, menuOpen]);

  // Fetch printers lazily when menu opens
  useEffect(() => {
    if (!menuOpen || printers !== null) return;
    getPrinters().then(r => setPrinters(r.data)).catch(() => setPrinters([]));
  }, [menuOpen]);

  const toggle = (key) => {
    const next = { ...visible, [key]: !visible[key] };
    setVisible(next);
    localStorage.setItem(`print_cols_${storageKey}`, JSON.stringify(next));
  };

  const activeCols = () => columns.filter(c => visible[c.key] !== false);

  const handleOpenPdf = async () => {
    const cols = activeCols();
    if (!cols.length || !onOpenTab) return;
    setMenuOpen(false);
    const blob = await buildTablePdf(title, cols, rows, size, infoLines);
    const url  = URL.createObjectURL(blob);
    onOpenTab(`print-pdf-${Date.now()}`, title, { type: 'print-pdf', pdfUrl: url });
  };

  const handleOpenChartPdf = async () => {
    if (!chartPdf || !onOpenTab) return;
    setMenuOpen(false);
    setSending(true);
    try {
      const colKeys = activeCols().map(c => c.key);
      const blob = await chartPdf(colKeys);
      const url  = URL.createObjectURL(blob);
      onOpenTab(`print-pdf-${Date.now()}`, title, { type: 'print-pdf', pdfUrl: url });
    } catch (e) { console.error(e); }
    setSending(false);
  };

  const handleSendToPrinter = async (printer) => {
    setMenuOpen(false);
    setSending(true);
    try {
      let blob;
      if (chartPdf) {
        const colKeys = activeCols().map(c => c.key);
        blob = await chartPdf(colKeys);
      } else {
        const cols = activeCols();
        if (!cols.length) { setSending(false); return; }
        blob = await buildTablePdf(title, cols, rows, size, infoLines);
      }
      const form = new FormData();
      form.append('pdf', blob, `${title}.pdf`);
      form.append('printer', printer);
      form.append('duplex', duplex ? '1' : '0');
      await submitPrintJob(form);
    } catch (err) {
      console.error('Print failed:', err);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="print-btn-wrap" ref={ref}>
      <button
        className="btn-icon print-col-btn"
        title="Column settings"
        onClick={() => { setMenuOpen(false); setColsOpen(v => !v); }}
      >
        <VscSettings size={15} />
      </button>
      <button
        className="btn-icon"
        title="Print"
        disabled={sending}
        onClick={() => { setColsOpen(false); setMenuOpen(v => !v); }}
      >
        <MdPrint size={16} />
      </button>

      {/* Column selector */}
      {colsOpen && (
        <div className="print-settings-panel">
          {!chartPdf && (
            <div className="print-size-row">
              <button className={`print-size-btn${size === 'a4' ? ' active' : ''}`} onClick={() => changeSize('a4')}>A4</button>
              <button className={`print-size-btn${size === 'a3' ? ' active' : ''}`} onClick={() => changeSize('a3')}>A3</button>
            </div>
          )}
          <div className="print-size-row" title="Applies when sending to a printer">
            <button className={`print-size-btn${!duplex ? ' active' : ''}`} onClick={() => changeDuplex(false)}>One-sided</button>
            <button className={`print-size-btn${duplex ? ' active' : ''}`} onClick={() => changeDuplex(true)}>Two-sided</button>
          </div>
          <div className="print-settings-title">Columns to print</div>
          <div className="print-settings-list">
            {columns.map(c => (
              <label key={c.key} className="print-settings-item">
                <input type="checkbox" checked={visible[c.key] !== false} onChange={() => toggle(c.key)} />
                {c.label}
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Print destination menu */}
      {menuOpen && (
        <div className="print-settings-panel">
          <button
            className="print-menu-item"
            onClick={chartPdf ? handleOpenChartPdf : handleOpenPdf}
            disabled={sending}
          >
            <BsFilePdf size={13} /> Open as PDF
          </button>
          {printers === null && (
            <p className="print-menu-muted">Loading printers…</p>
          )}
          {printers !== null && printers.length > 0 && (
            <>
              <div className="print-menu-sep" />
              {printers.map(p => (
                <button key={p.name} className="print-menu-item" onClick={() => handleSendToPrinter(p.name)} disabled={sending}>
                  <MdPrint size={13} /> {p.displayName || p.name}
                </button>
              ))}
            </>
          )}
          {printers !== null && printers.length === 0 && (
            <p className="print-menu-muted">No printers available</p>
          )}
        </div>
      )}
    </div>
  );
}

export default PrintButton;
